import type { PrintableProviderValue } from '@/Ship/Contracts/PrintableProvider'

/**
 * Генерація документа з шаблону у вкладці «Друковані форми» (`SPEC-012` §9.2,
 * §9.3): що кому показувати і як підписано.
 */
export interface CrmDocumentGenerationConfig {
    /** Провайдер типів документа, які годяться власнику (N-19) */
    provider: PrintableProviderValue
    /** Прапорець типу «Використовувати в картці …» / «… в заявках» (P-12) */
    useInFlag: 'isUseInClient' | 'isUseInCarrier' | 'isUseInApplication'
    /**
     * Колекція договорів власника — звідти номер і дата (P-16). У заявки
     * договорів немає: номер документа за замовчуванням — її Ід (`SPEC-021` §8.8)
     */
    contractsRestUrl: string | null
    /** Підпис поля юрособи: у клієнта «Юридична особа», у перевізника «Експедитор» */
    legalEntityLabel: string
    /**
     * Поля «Номер договору» й «Дата договору» в шухляді — лише заявка
     * (`SPEC-025` §3.1.1; легасі `AddButtonApplicationDocumentCrud.jsx`); бек
     * клієнту й перевізнику їх не приймає (N-18)
     */
    withContractTerms: boolean
    /** Поле власника, значення якого — номер документа за замовчуванням (заявка — `id`) */
    defaultNumberField: string | null
}

/**
 * Налаштування за полем власника в документі (`client`, `carrier`). Поле
 * власника збігається зі значенням провайдера друку — тож вкладці документів
 * досить того, що вона й так знає.
 */
export const CRM_DOCUMENT_GENERATION: Readonly<Record<string, CrmDocumentGenerationConfig>> = {
    client: {
        provider: 'client',
        useInFlag: 'isUseInClient',
        contractsRestUrl: '/services/client_contracts',
        legalEntityLabel: 'Юридична особа',
        withContractTerms: false,
        defaultNumberField: null,
    },
    carrier: {
        provider: 'carrier',
        useInFlag: 'isUseInCarrier',
        contractsRestUrl: '/services/carrier_contracts',
        legalEntityLabel: 'Експедитор',
        withContractTerms: false,
        defaultNumberField: null,
    },
    application: {
        provider: 'application',
        useInFlag: 'isUseInApplication',
        contractsRestUrl: null,
        // Легасі `AddButtonApplicationDocumentCrud.jsx` — `t('Legal entity')`
        legalEntityLabel: 'Юридична особа',
        withContractTerms: true,
        defaultNumberField: 'id',
    },
}

/** Тип документа так, як він потрібен меню генерації. */
export interface GenerationDocumentType {
    uuid: string
    title: string
}

/** Тексти легасі (`uk.json:45,92,257,262-264,404-405,549-550`) і нові (N-11). */
export const GENERATE_DOCUMENT_TEXTS = {
    add: 'Додати документ',
    menuGroup: 'Генерація документу',
    moreLabel: 'Згенерувати документ з шаблону',
    number: 'Номер документу',
    date: 'Дата документа',
    isStamp: 'З печаткою та підписом',
    contractNumber: 'Номер договору',
    contractDate: 'Дата договору',
    generate: 'Згенерувати',
    close: 'Закрити',
    successTitle: 'Успіх',
    success: (title: string): string => `Документ ${title} успішно згенеровано.`,
    errorTitle: 'Помилка',
    contactAdmin: 'Не вдалося згенерувати документ. Зверніться до адміністратора системи.',
} as const

/** Межа номера документа — та сама, що в беку (`AbstractDocumentEntity::NUMBER_MAX_LENGTH`). */
export const DOCUMENT_NUMBER_MAX_LENGTH = 139

/** Межа номера договору — та сама, що в беку (`GenerateDocumentAction::CONTRACT_NUMBER_MAX_LENGTH`). */
export const CONTRACT_NUMBER_MAX_LENGTH = 150
